import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeading from '@/components/base/PageHeading';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsArticles } from '@/mocks/newsArticles';
import { daysBetween, formatDateCn, splitDate } from '@/utils/format';

const PAGE_SIZE = 4;
const ranges = [
  { key: 'all', label: '全部时间', days: 9999 },
  { key: 'week', label: '近一周', days: 7 },
  { key: 'month', label: '近一月', days: 30 },
];

export default function NewsCategory() {
  const [range, setRange] = useState('all');
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: '最新动态与幕后资讯 - 禁止的爱善良的小姨子电视剧专题',
    description:
      '禁止的爱善良的小姨子最新动态，包含收官收视、主创访谈、老宅场景搭建花絮与专题策划，按发布时间倒序整理。',
    keywords: '禁止的爱善良的小姨子动态,影视资讯,幕后花絮,主创访谈',
  });

  const latestDate = newsArticles[0]?.date ?? '2026-10-16';

  const filtered = useMemo(() => {
    const active = ranges.find((item) => item.key === range) ?? ranges[0];
    return [...newsArticles]
      .filter((article) => daysBetween(article.date, latestDate) <= active.days)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [range, latestDate]);

  const shown = filtered.slice(0, visible);

  return (
    <SiteLayout>
      <PageHeading
        crumbs={[{ label: '动态' }]}
        eyebrow="NEWSROOM"
        title="最新动态与幕后资讯"
        description="从开播到收官，关于这部剧的消息都在这里：收视数据、主创访谈、场景搭建花絮，以及编辑部整理的专题内容。"
        count={`${newsArticles.length} 篇 已发布`}
      />

      <section className="bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center text-base text-primary-500">
                <i className="ri-calendar-line" />
              </span>
              按发布时间倒序 · 最近更新 {formatDateCn(latestDate)}
            </div>
            <div className="flex items-center gap-1 self-start rounded-full bg-background-100 p-1">
              {ranges.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setRange(item.key);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                    range === item.key
                      ? 'bg-primary-600 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="mt-8">
              <EmptyState
                title="暂无该时间段内的动态"
                description="换个时间范围看看，或返回首页浏览最近的剧集资讯。"
                actionLabel="查看全部动态"
                onAction={() => {
                  setRange('all');
                  setVisible(PAGE_SIZE);
                }}
              />
            </div>
          ) : (
            <>
              <div className="mt-6 divide-y divide-background-200 border-y border-background-200">
                {shown.map((article) => {
                  const { month, day, year } = splitDate(article.date);
                  return (
                    <article key={article.id} className="group py-5">
                      <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
                        <Link
                          to={`/detail/news/${article.id}`}
                          className="h-[180px] w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-[150px] sm:w-[230px]"
                        >
                          <img
                            src={article.image}
                            alt={article.title}
                            title={`${article.title} - 禁止的爱善良的小姨子动态`}
                            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                          />
                        </Link>
                        <div className="flex min-w-0 flex-1 flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-3 text-[11px]">
                              <span className="rounded-sm bg-secondary-100 px-2 py-0.5 text-secondary-900">
                                {article.category}
                              </span>
                              <span className="text-foreground-400">
                                {year}年{month}月{day}日
                              </span>
                            </div>
                            <h2 className="mt-2 font-heading text-base leading-snug text-foreground-900 transition-colors group-hover:text-primary-600 md:text-lg">
                              <Link to={`/detail/news/${article.id}`}>{article.title}</Link>
                            </h2>
                            <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500 md:text-[13px]">
                              {article.summary}
                            </p>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {article.tags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full border border-background-300 px-2.5 py-0.5 text-[11px] text-foreground-500"
                              >
                                {tag}
                              </span>
                            ))}
                            <Link
                              to={`/detail/news/${article.id}`}
                              className="ml-auto flex items-center gap-1 whitespace-nowrap text-xs text-accent-700 transition-colors hover:text-accent-800"
                            >
                              阅读全文
                              <span className="flex h-3.5 w-3.5 items-center justify-center transition-transform group-hover:translate-x-0.5">
                                <i className="ri-arrow-right-line" />
                              </span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {visible < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => Math.min(prev + PAGE_SIZE, filtered.length))}
                    className="whitespace-nowrap rounded-md border border-foreground-300 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600"
                  >
                    加载更多动态
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已展示全部 {filtered.length} 条动态</p>
                )}
                <p className="text-[11px] text-foreground-400">时间筛选与分页为前端演示状态</p>
              </div>
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}